import { Suspense, useEffect, useState } from 'react';
import {
  useLocation, useNavigate, useSearchParams,
} from 'react-router-dom';
import {
  Grid, Layout, message, Spin,
} from 'taxaroo-ui';
import { useAppDispatch, useAppSelector } from '~src/redux/hooks';
import { hamburgerMenuOpen } from '~src/cache';
import LeftSideMenu from '~src/components/organisms/LeftSideMenu';
import TopHeader from '~src/components/organisms/TopHeader';
import LoadingIndicator from '~src/components/atoms/LoadingIndicator';
import { selectClient, setClientData } from '~src/redux/slices/clientSlice';
import { useLoginAsFirmMutation } from '~src/graphql/mutations/login';
import { loginAsPreparer } from '~src/pages/Login/Login';
import { UserFirmAccountEntity } from '~src/graphql';
import { setPreviousAccessToken } from '~src/redux/slices/sessionSlice';
import { useCurrentStripeInformationQuery, useIsActiveFirmQuery } from '~src/graphql/queries/settings';
import PayWall from '~src/pages/PayWall';
import { isMobileView } from '~src/components/helpers/screen';
import UpgradeModal from '~src/components/atoms/UpgradeModal';
import { useUpgradeModal, Features } from '~src/components/atoms/UpgradeModal/ModalProvider';
import { trackFinishedOnboarding } from '~src/components/helpers/customer.io';
import AnnouncementBar from '../AnnouncementBar';
import AiAssistant from '../AIAssistant';

const { useBreakpoint } = Grid;
const { Content } = Layout;

interface Upvoty {
  init: (action: string, config: any) => void;
  // You can add more methods or properties of upvoty if needed
}
declare global {
  interface Window {
    upvoty: Upvoty;
  }
}

interface PrivateRouteProps {
  children: React.ReactNode;
}

const PrivateRoute = ({ children }: PrivateRouteProps) => {
  const {
    accessToken,
    firmCreatedAt,
    email,
    firstName,
    lastName,
    userId,
  } = useAppSelector((state) => state.session);
  const screens = useBreakpoint();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const clientState = useAppSelector(selectClient);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const userIdParam = searchParams.get('userId');
  const tokenParam = searchParams.get('token');
  const firmIdParam = searchParams.get('firmId');
  const { data: isActiveFirm, loading: isActiveFirmLoading } = useIsActiveFirmQuery();
  const { data: currentStripeInformation } = useCurrentStripeInformationQuery();
  const { customerId, finishedOnboarding } = currentStripeInformation?.CurrentStripeInformation ?? {};

  const { showModal, accessObject } = useUpgradeModal();

  const [isLeftMenuCollapsed, setIsLeftMenuCollapsed] = useState(false);
  let isLeftSideMenuCollapsed = isLeftMenuCollapsed;
  const setIsLeftSideMenuCollapsed = (val: boolean) => {
    isLeftSideMenuCollapsed = val;
  };

  const [loginAsFirm] = useLoginAsFirmMutation({
    onError: (err) => message.error(`Login as Firm error: ${err.message}`, 5),
  });
  const handleLoginAsFirm = async () => {
    const result = await loginAsFirm({
      variables: {
        token: tokenParam,
        userId: userIdParam,
        firmId: firmIdParam,
      },
    });

    if (result.data?.LoginAsFirm?.accessToken) {
      const { accessToken: accessTokenLoginAs, UserFirmAccount } = result.data.LoginAsFirm;
      loginAsPreparer(dispatch, accessTokenLoginAs, UserFirmAccount as UserFirmAccountEntity);
      dispatch(setPreviousAccessToken({ accessToken }));
      navigate('/', { replace: true });
    }
  };

  useEffect(() => {
    hamburgerMenuOpen(false);
  }, [location.key]);

  useEffect(() => {
    if (Object.values(clientState).some((val) => val) && location.pathname !== '/clients/details') {
      dispatch(setClientData({
        id: undefined,
        email: undefined,
        lastName: undefined,
        entityId: undefined,
        firstName: undefined,
        phoneNumber: undefined,
        jobs: [],
        job: undefined,
      }));
    }
  }, [location.pathname]);

  // login as firm
  useEffect(() => {
    if (tokenParam && userIdParam) {
      handleLoginAsFirm();
    }
  }, []);

  // load upvoty
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://taxaroo.upvoty.com/javascript/upvoty.embed.js';
    script.async = true;

    script.onload = () => {
      // eslint-disable-next-line
      if (typeof window.upvoty !== 'undefined') {
        window.upvoty.init('identify', {
          user: {
            id: userId,
            name: `${firstName} ${lastName}`,
            email,
          },
          baseUrl: 'taxaroo.upvoty.com',
          publicKey: '4d2a655bc7f2672b0b731a56a8f2a2ca',
        });
      }
    };

    document.head.appendChild(script);

    // Cleanup: remove the script when the component unmounts
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  // load Stunning
  useEffect(() => {
    const loadStunning = () => {
      // Check if script is already loaded to prevent duplicates
      if (document.getElementById('stunning-bar')) return;

      const script = document.createElement('script');
      script.id = 'stunning-bar';
      script.src = 'https://d1gqkepxkcxgvm.cloudfront.net/stunning-bar.js';
      script.defer = true;
      script.setAttribute('data-app-ckey', '4461kfruqgwcvoyjpsninppsp');
      script.setAttribute('data-stripe-id', customerId);

      const s = document.getElementsByTagName('script')[0];
      s.parentNode?.insertBefore(script, s);
    };

    loadStunning();
    trackFinishedOnboarding(finishedOnboarding);

    // Cleanup: remove the script when the component unmounts or accessToken changes
    return () => {
      const script = document.getElementById('stunning-bar');
      if (script) {
        script.parentNode?.removeChild(script);
      }
    };
  }, [accessToken]);

  if (!accessToken) {
    navigate('/login', { state: { from: location } });
    return null;
  }

  if (isActiveFirmLoading) {
    return (
      <div style={{
        height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      >
        <div style={{
          height: '100px', width: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        >
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!isActiveFirmLoading && !isActiveFirm.isActiveFirm) {
    return (
      <PayWall customerId={customerId} />
    );
  }

  return (
    <Layout>
      {customerId && <AnnouncementBar customerId={customerId} />}
      <TopHeader />
      <Layout style={{ minHeight: 'calc(100vh - 100px)' }}>
        {screens.md
          ? <LeftSideMenu stateChanged={(isCollapsed) => setIsLeftMenuCollapsed(isCollapsed)} />
          : setIsLeftSideMenuCollapsed(true)}
        <Layout style={{ padding: 0 }}>
          <Content
            style={{
              background: '#fff',
              margin: 0,
              minHeight: 280,
              padding: isMobileView() ? 16 : 24,
            }}
          >
            <Suspense
              fallback={(
                <div
                  style={{ display: 'flex', justifyContent: 'center', alignContent: 'flex-start' }}
                >
                  <LoadingIndicator size="calc(100vh - 100px)" type="full" id="loading_jobs" />
                </div>
                )}
            >
              {children}
            </Suspense>
          </Content>
        </Layout>
      </Layout>
      <a
        href="https://taxaroo.upvoty.com/b/feature-requests/"
        target="_blank"
        rel="noreferrer"
        style={{
          position: 'fixed',
          bottom: 22,
          right: 6,
          zIndex: 999,
          color: 'gray',
          fontSize: 14,
        }}
      >
        Feature request?
      </a>
      <UpgradeModal />
      {Boolean(accessObject?.[Features.aiResearch]) && (
        <AiAssistant isLeftSideMenuCollapsed={isLeftSideMenuCollapsed} />
      )}
    </Layout>
  );
};

export default PrivateRoute;
